import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../api";

// Platform admin: the doctor self sign-up free-trial campaign (mindconnect.in/signup).
// Backend: training BE app/organization/signup_routes.py (GET/PUT /admin/trial-campaign).
export default function TrialCampaignCard() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.fetchTrialCampaign().then((res) => setSettings(res.data)).catch(() => setSettings(null));
  }, []);

  if (!settings) return null;

  const chosen = new Set(settings.features || []);
  const toggleFeature = (key) => {
    const next = new Set(chosen);
    next.has(key) ? next.delete(key) : next.add(key);
    setSettings({ ...settings, features: [...next] });
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.updateTrialCampaign({
        enabled: settings.enabled,
        trial_days: Number(settings.trial_days),
        ai_sessions_per_day: Number(settings.ai_sessions_per_day),
        features: settings.features || [],
      });
      setSettings(res.data);
      toast.success("Free-trial campaign saved");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Could not save the campaign settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Free-trial campaign</h2>
          <p className="text-sm text-gray-500">Doctors can sign up themselves at mindconnect.in/signup. Trials get basic + AI add-on, 1 seat.</p>
        </div>
        <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer whitespace-nowrap">
          <input type="checkbox" className="h-4 w-4" checked={!!settings.enabled} onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })} />
          Sign-up open
        </label>
      </div>

      <div className="mt-4 flex flex-wrap items-end gap-4">
        <div>
          <label htmlFor="trial_days" className="block text-xs font-medium text-gray-600 mb-1">Trial days</label>
          <input id="trial_days" type="number" min="1" max="90" value={settings.trial_days} onChange={(e) => setSettings({ ...settings, trial_days: e.target.value })}
            className="w-28 rounded-md border border-gray-300 px-3 py-2 text-sm" />
        </div>
        <div>
          <label htmlFor="trial_ai_per_day" className="block text-xs font-medium text-gray-600 mb-1">Free AI sessions / day</label>
          <input id="trial_ai_per_day" type="number" min="0" max="100" value={settings.ai_sessions_per_day} onChange={(e) => setSettings({ ...settings, ai_sessions_per_day: e.target.value })}
            className="w-28 rounded-md border border-gray-300 px-3 py-2 text-sm" />
        </div>
        <button type="button" onClick={save} disabled={saving}
          className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-60 text-sm font-medium">
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
      {/* Practice features a new trial starts with (organization/trial_signup.py TRIAL_FEATURES). */}
      <div className="mt-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-800">Features switched on for new trials ({chosen.size} of {(settings.available_features || []).length})</h3>
          <div className="flex gap-3 text-xs">
            <button type="button" className="text-blue-600 hover:underline" onClick={() => setSettings({ ...settings, features: (settings.available_features || []).map((f) => f.key) })}>Select all</button>
            <button type="button" className="text-blue-600 hover:underline" onClick={() => setSettings({ ...settings, features: [] })}>Clear</button>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-0.5">"Care-team access to clinical notes" is off by default: in a one-doctor practice it only hides a new patient's notes until their first appointment. Session audio recording costs us per call.</p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {(settings.available_features || []).map((f) => (
            <label key={f.key} htmlFor={`trial-f-${f.key}`} className="normal-case font-sans tracking-normal flex items-start gap-2.5 rounded-lg border border-gray-200 p-2.5 cursor-pointer hover:bg-gray-50">
              <input id={`trial-f-${f.key}`} type="checkbox" className="h-4 w-4 mt-0.5" checked={chosen.has(f.key)} onChange={() => toggleFeature(f.key)} />
              <span>
                <span className="block text-sm font-medium text-gray-800">{f.label}</span>
                <span className="block text-xs text-gray-500">{f.description}</span>
              </span>
            </label>
          ))}
        </div>
      </div>
      <p className="mt-3 text-xs text-gray-400">Changes apply to new sign-ups; existing trials keep the terms and features they started with.</p>
    </div>
  );
}
